import { useState, useEffect, useRef } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Gear, 
  WhatsappLogo, 
  CheckCircle, 
  XCircle, 
  PaperPlaneTilt,
  CurrencyDollar,
  Translate,
  EnvelopeSimple,
  PaintBrush,
  Image,
  Upload,
  Globe,
  Copy,
  ArrowClockwise,
  Trash,
  ShieldCheck,
  MapPin,
  Warehouse,
  Truck,
  User,
  Eye
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { getWhatsAppSettings, testWhatsApp, getPricing, getEmailSettings, testEmail, getBranding, updateBranding, uploadLogo, getDomain, setDomain, verifyDomain, removeDomain } from '../lib/api';
import api from '../lib/api';
import PartnersSettings from '../components/PartnersSettings';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

export default function SettingsPage() {
  const { t, language, toggleLanguage, setLanguage, tx } = useLanguage();
  const [whatsappSettings, setWhatsappSettings] = useState(null);
  const [emailSettings, setEmailSettings] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [pricingDraft, setPricingDraft] = useState({ price_per_kg: '', small_package_threshold_kg: '', small_package_flat_fee: '', small_package_mode: 'per_kg' });
  const [savingPricing, setSavingPricing] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [branding, setBranding] = useState({ logo_url: '', primary_color: '#FF9900', price_per_pound: 5.5, company_name: '' });
  const [savingBranding, setSavingBranding] = useState(false);
  const logoInputRef = useRef(null);
  const [domainData, setDomainData] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainVerifying, setDomainVerifying] = useState(false);
  const [domainHasFeature, setDomainHasFeature] = useState(false);
  const [locations, setLocations] = useState({
    warehouse_name: 'Miami Warehouse', warehouse_city: 'Miami, FL',
    destination_name: 'Destination', destination_city: '',
    currency: 'USD', weight_unit: 'lbs', price_per_unit: 4.50,
  });
  const [currencies, setCurrencies] = useState({});
  const [savingLocations, setSavingLocations] = useState(false);
  const [onboardSettings, setOnboardSettings] = useState({
    welcome_message: 'Welcome! Sign up to start tracking your packages with us.',
    form_heading: 'Create Your Account',
    success_message: "You're all set! You can now submit and track your packages.",
    show_address: true,
    show_city: true,
  });
  const [onboardLink, setOnboardLink] = useState('');
  const [savingOnboard, setSavingOnboard] = useState(false);
  const [invoiceSettings, setInvoiceSettings] = useState({
    footer_text: '',
    notes_template: '',
    show_exchange_rate: true,
    show_crc_total: true,
  });
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [statusNotify, setStatusNotify] = useState({
    submitted: false, intake_miami: true, in_transit: true,
    arrived_san_jose: true, ready_for_pickup: true, delivered: true,
  });
  const [submissionNotifyEmails, setSubmissionNotifyEmails] = useState([]);
  const [submissionEmailInput, setSubmissionEmailInput] = useState('');
  const [adminAlertEmails, setAdminAlertEmails] = useState([]);
  const [adminAlertEmailInput, setAdminAlertEmailInput] = useState('');
  const [notifyOnNewPackage, setNotifyOnNewPackage] = useState(true);
  const [notifyOnNewCustomer, setNotifyOnNewCustomer] = useState(true);
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestHour, setDigestHour] = useState(8);
  const [digestTimezone, setDigestTimezone] = useState('America/Costa_Rica');
  const [digestLastSent, setDigestLastSent] = useState(null);
  const [sendingDigestNow, setSendingDigestNow] = useState(false);
  const [savingNotify, setSavingNotify] = useState(false);
  const [apiKeys, setApiKeys] = useState([]);
  const [generatedKey, setGeneratedKey] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [paymentAccounts, setPaymentAccounts] = useState({
    stripe_connected: false, paypal_connected: false, preferred_payment: 'stripe',
    stripe_key_hint: '', paypal_id_hint: '', paypal_mode: 'sandbox',
  });
  const [paymentForm, setPaymentForm] = useState({
    stripe_api_key: '', paypal_client_id: '', paypal_secret: '', paypal_mode: 'sandbox', preferred_payment: 'stripe',
  });
  const [savingPayment, setSavingPayment] = useState(false);

  // Duplicate-invoice cleanup tool
  const [dupeReport, setDupeReport] = useState(null);
  const [scanningDupes, setScanningDupes] = useState(false);
  const [cancellingDupes, setCancellingDupes] = useState(false);

  const scanForDuplicateInvoices = async () => {
    setScanningDupes(true);
    try {
      const r = await api.get('/invoices/find-duplicates');
      setDupeReport(r.data);
      if ((r.data.groups || []).length === 0) {
        toast.success(tx('No duplicate invoices found 🎉', 'No hay facturas duplicadas 🎉', 'Aucune facture en double 🎉'));
      } else {
        toast.info(tx(
          `Found ${r.data.duplicate_invoice_count} duplicate invoice(s) across ${r.data.affected_customers} customer(s)`,
          `${r.data.duplicate_invoice_count} factura(s) duplicada(s) en ${r.data.affected_customers} cliente(s)`,
          `${r.data.duplicate_invoice_count} doublons trouvés`
        ), { duration: 6000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || tx('Scan failed', 'Error al escanear', 'Échec'));
    } finally {
      setScanningDupes(false);
    }
  };

  const cancelDuplicateInvoices = async (invoiceIds, label) => {
    if (!invoiceIds || invoiceIds.length === 0) return;
    if (!window.confirm(tx(
      `Cancel ${invoiceIds.length} duplicate invoice(s)?\n\nThe original invoice is kept. Packages on cancelled invoices are released so they remain on the kept invoice.`,
      `¿Cancelar ${invoiceIds.length} factura(s) duplicada(s)? Se conserva la original.`,
      `Annuler ${invoiceIds.length} factures en double ? L'original est conservé.`
    ))) return;
    setCancellingDupes(true);
    try {
      const r = await api.post('/invoices/cancel-duplicates', { invoice_ids: invoiceIds });
      toast.success(tx(
        `${r.data.cancelled_count} cancelled · ${r.data.skipped_count} skipped · ${r.data.error_count} errors${label ? ` (${label})` : ''}`,
        `${r.data.cancelled_count} canceladas · ${r.data.skipped_count} omitidas · ${r.data.error_count} errores`,
        `${r.data.cancelled_count} annulées · ${r.data.skipped_count} ignorées · ${r.data.error_count} erreurs`
      ), { duration: 7000 });
      await scanForDuplicateInvoices();
    } catch (e) {
      toast.error(e.response?.data?.detail || tx('Cleanup failed', 'Error', 'Échec'));
    } finally {
      setCancellingDupes(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [whatsappRes, pricingRes, emailRes, brandingRes, domainRes, locRes, onboardRes, invoiceRes, keysRes, whRes, notifyRes] = await Promise.all([
        getWhatsAppSettings(),
        getPricing(),
        getEmailSettings(),
        getBranding(),
        getDomain().catch(() => ({ data: { custom_domain: null, has_feature: false } })),
        api.get('/portal/settings/locations').catch(() => ({ data: { locations: {}, currencies: {} } })),
        api.get('/portal/settings/onboard-form').catch(() => ({ data: { settings: {}, company_id: '' } })),
        api.get('/portal/settings/invoice').catch(() => ({ data: { settings: {} } })),
        api.get('/v1/keys').catch(() => ({ data: { keys: [] } })),
        api.get('/v1/webhooks/config').catch(() => ({ data: { url: '', events: [], active: false } })),
        api.get('/portal/settings/notifications').catch(() => ({ data: { status_emails: {} } })),
      ]);
      setWhatsappSettings(whatsappRes.data);
      setPricing(pricingRes.data);
      setPricingDraft({
        price_per_kg: String(pricingRes.data.price_per_kg ?? ''),
        small_package_threshold_kg: String(pricingRes.data.small_package_threshold_kg ?? ''),
        small_package_flat_fee: String(pricingRes.data.small_package_flat_fee ?? ''),
        small_package_mode: pricingRes.data.small_package_mode || 'per_kg',
      });
      setEmailSettings(emailRes.data);
      setBranding(brandingRes.data);
      setDomainData(domainRes.data.custom_domain);
      setDomainHasFeature(domainRes.data.has_feature);
      if (domainRes.data.custom_domain?.domain) {
        setDomainInput(domainRes.data.custom_domain.domain);
      }
      if (locRes.data.locations) setLocations(locRes.data.locations);
      if (locRes.data.currencies) setCurrencies(locRes.data.currencies);
      if (onboardRes.data.settings) setOnboardSettings(prev => ({ ...prev, ...onboardRes.data.settings }));
      if (onboardRes.data.company_id) setOnboardLink(`${window.location.origin}/join/${onboardRes.data.company_id}`);
      if (invoiceRes.data.settings) setInvoiceSettings(prev => ({ ...prev, ...invoiceRes.data.settings }));
      if (keysRes.data.keys) setApiKeys(keysRes.data.keys);
      if (whRes.data.url) { setWebhookUrl(whRes.data.url); setWebhookEvents(whRes.data.events || []); }
      if (notifyRes.data.status_emails) setStatusNotify(prev => ({ ...prev, ...notifyRes.data.status_emails }));
      if (Array.isArray(notifyRes.data.submission_notify_emails)) setSubmissionNotifyEmails(notifyRes.data.submission_notify_emails);
      if (Array.isArray(notifyRes.data.admin_emails)) setAdminAlertEmails(notifyRes.data.admin_emails);
      if (typeof notifyRes.data.notify_on_new_package === 'boolean') setNotifyOnNewPackage(notifyRes.data.notify_on_new_package);
      if (typeof notifyRes.data.notify_on_new_customer === 'boolean') setNotifyOnNewCustomer(notifyRes.data.notify_on_new_customer);
      if (typeof notifyRes.data.daily_digest_enabled === 'boolean') setDigestEnabled(notifyRes.data.daily_digest_enabled);
      if (typeof notifyRes.data.daily_digest_hour === 'number') setDigestHour(notifyRes.data.daily_digest_hour);
      if (notifyRes.data.daily_digest_timezone) setDigestTimezone(notifyRes.data.daily_digest_timezone);
      if (notifyRes.data.daily_digest_last_sent_date) setDigestLastSent(notifyRes.data.daily_digest_last_sent_date);
      const paRes = await api.get('/portal/settings/payment-accounts').catch(() => ({ data: {} }));
      if (paRes.data) {
        setPaymentAccounts(paRes.data);
        setPaymentForm(prev => ({ ...prev, preferred_payment: paRes.data.preferred_payment || 'stripe', paypal_mode: paRes.data.paypal_mode || 'sandbox' }));
      }
    } catch (error) {
      console.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      await updateBranding({
        primary_color: branding.primary_color,
        price_per_pound: branding.price_per_pound,
        company_name: branding.company_name,
      });
      toast.success(tx('Branding updated', 'Marca actualizada', 'Image de marque mise à jour'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await uploadLogo({ logo_data: reader.result });
        setBranding(prev => ({ ...prev, logo_url: res.data.logo_url }));
        toast.success(tx('Logo uploaded', 'Logo subido', 'Logo téléchargé'));
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload logo');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSetDomain = async () => {
    if (!domainInput.trim()) {
      toast.error(tx('Enter a domain', 'Ingresa un dominio', 'Entrez un domaine'));
      return;
    }
    setDomainLoading(true);
    try {
      const res = await setDomain({ domain: domainInput.trim() });
      setDomainData(res.data.custom_domain);
      toast.success(tx('Domain saved! Configure your DNS records below.', 'Dominio guardado! Configura tus registros DNS.', 'Domaine enregistré ! Configurez vos enregistrements DNS.'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save domain');
    } finally {
      setDomainLoading(false);
    }
  };

  const handleVerifyDomain = async () => {
    setDomainVerifying(true);
    try {
      const res = await verifyDomain();
      const newStatus = res.data.status;
      setDomainData(prev => ({ ...prev, status: newStatus }));
      if (newStatus === 'verified') {
        toast.success(tx('Domain verified successfully!', 'Dominio verificado!', 'Domaine vérifié !'));
      } else if (newStatus === 'txt_verified') {
        toast.info(tx('TXT verified, but CNAME not yet detected. It may take time to propagate.', 'TXT verificado, pero CNAME aún no detectado.', 'TXT vérifié, mais CNAME pas encore détecté.'));
      } else {
        toast.error(tx('DNS records not yet detected. Check your configuration and try again.', 'Registros DNS no detectados. Revisa tu configuración.', 'Enregistrements DNS non détectés. Vérifiez votre configuration.'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setDomainVerifying(false);
    }
  };

  const handleRemoveDomain = async () => {
    try {
      await removeDomain();
      setDomainData(null);
      setDomainInput('');
      toast.success(tx('Custom domain removed', 'Dominio personalizado eliminado', 'Domaine personnalisé supprimé'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove domain');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(tx('Copied!', 'Copiado!', 'Copié !'));
  };

  const handleSaveLocations = async () => {
    setSavingLocations(true);
    try {
      await api.post('/portal/settings/locations', locations);
      toast.success(tx('Locations & currency saved!', 'Ubicaciones y moneda guardadas!', 'Emplacements et devise enregistrés !'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSavingLocations(false); }
  };

  const handleSaveOnboard = async () => {
    setSavingOnboard(true);
    try {
      await api.post('/portal/settings/onboard-form', onboardSettings);
      toast.success(tx('Onboard form settings saved!', 'Configuración del formulario guardada!', 'Paramètres du formulaire enregistrés !'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSavingOnboard(false); }
  };

  const handleSaveInvoice = async () => {
    setSavingInvoice(true);
    try {
      await api.post('/portal/settings/invoice', invoiceSettings);
      toast.success(tx('Invoice settings saved!', 'Configuración de factura guardada!', 'Paramètres de facture enregistrés !'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSavingInvoice(false); }
  };

  const handleSaveNotify = async () => {
    setSavingNotify(true);
    try {
      await api.post('/portal/settings/notifications', {
        ...statusNotify,
        submission_notify_emails: submissionNotifyEmails,
        admin_emails: adminAlertEmails,
        notify_on_new_package: notifyOnNewPackage,
        notify_on_new_customer: notifyOnNewCustomer,
        daily_digest_enabled: digestEnabled,
        daily_digest_hour: digestHour,
        daily_digest_timezone: digestTimezone,
      });
      toast.success(tx('Notification preferences saved!', '¡Preferencias guardadas!', 'Préférences enregistrées !'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSavingNotify(false); }
  };

  const handleSavePricing = async () => {
    setSavingPricing(true);
    try {
      const payload = {
        price_per_kg: pricingDraft.price_per_kg !== '' ? Number(pricingDraft.price_per_kg) : null,
        small_package_threshold_kg: pricingDraft.small_package_threshold_kg !== '' ? Number(pricingDraft.small_package_threshold_kg) : null,
        small_package_flat_fee: pricingDraft.small_package_flat_fee !== '' ? Number(pricingDraft.small_package_flat_fee) : null,
        small_package_mode: pricingDraft.small_package_mode || 'per_kg',
      };
      const res = await api.put('/settings/pricing', payload);
      setPricing(res.data);
      toast.success(tx('Pricing saved!', '¡Tarifa guardada!', 'Tarif enregistré !'));
    } catch (err) {
      toast.error(err.response?.data?.detail || tx('Failed to save pricing', 'Error al guardar tarifa', 'Échec'));
    } finally {
      setSavingPricing(false);
    }
  };

  const addSubmissionEmail = () => {
    const email = submissionEmailInput.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(tx('Enter a valid email', 'Ingresa un correo válido', 'Saisissez un email valide'));
      return;
    }
    if (submissionNotifyEmails.includes(email)) {
      toast.warning(tx('Email already added', 'Correo ya agregado', 'Email déjà ajouté'));
      return;
    }
    if (submissionNotifyEmails.length >= 10) {
      toast.error(tx('Maximum 10 recipients', 'Máximo 10 correos', 'Maximum 10 destinataires'));
      return;
    }
    setSubmissionNotifyEmails(prev => [...prev, email]);
    setSubmissionEmailInput('');
  };

  const removeSubmissionEmail = (email) => {
    setSubmissionNotifyEmails(prev => prev.filter(e => e !== email));
  };

  const handleGenerateApiKey = async () => {
    try {
      const res = await api.post('/v1/keys/generate');
      setGeneratedKey(res.data.api_key);
      const keysRes = await api.get('/v1/keys');
      setApiKeys(keysRes.data.keys || []);
      toast.success('API key generated! Save it now — it won\'t be shown again.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate key'); }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await api.delete(`/v1/keys/${keyId}`);
      setApiKeys(prev => prev.filter(k => k.id !== keyId));
      toast.success('API key revoked');
    } catch (err) { toast.error('Failed to revoke key'); }
  };

  const handleSaveWebhook = async () => {
    setSavingWebhook(true);
    try {
      await api.post('/v1/webhooks/config', { url: webhookUrl, events: webhookEvents });
      toast.success('Webhook saved!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save webhook'); }
    finally { setSavingWebhook(false); }
  };

  const handleDeleteWebhook = async () => {
    try {
      await api.delete('/v1/webhooks/config');
      setWebhookUrl(''); setWebhookEvents([]);
      toast.success('Webhook removed');
    } catch (err) { toast.error('Failed to remove webhook'); }
  };

  const handleSavePaymentAccounts = async () => {
    setSavingPayment(true);
    try {
      await api.put('/portal/settings/payment-accounts', paymentForm);
      toast.success(tx('Payment accounts updated!', 'Cuentas de pago actualizadas!', 'Payment accounts updated!'));
      const res = await api.get('/portal/settings/payment-accounts');
      setPaymentAccounts(res.data);
      setPaymentForm(prev => ({ ...prev, stripe_api_key: '', paypal_client_id: '', paypal_secret: '' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save payment accounts');
    } finally { setSavingPayment(false); }
  };

  const handleTestWhatsApp = async () => {
    if (!testPhone.trim()) {
      toast.error(tx('Enter a phone number', 'Ingresa un número de teléfono', 'Enter a phone number'));
      return;
    }
    
    setTesting(true);
    try {
      await testWhatsApp(testPhone);
      toast.success(tx('Test message sent!', '¡Mensaje de prueba enviado!', 'Test message sent!'));
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Failed to send', 'Error al enviar', 'Failed to send')));
    } finally {
      setTesting(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddr.trim()) {
      toast.error(tx('Enter an email address', 'Ingresa un correo electrónico', 'Enter an email address'));
      return;
    }
    
    setTestingEmail(true);
    try {
      await testEmail(testEmailAddr);
      toast.success(tx('Test email sent!', '¡Email de prueba enviado!', 'Test email sent!'));
    } catch (error) {
      toast.error(error.response?.data?.detail || (tx('Failed to send email', 'Error al enviar email', 'Failed to send email')));
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <Layout title={tx('Settings', 'Configuración', 'Paramètres')}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0077FF] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={tx('Settings', 'Configuración', 'Paramètres')}>
      <div className="space-y-6 max-w-2xl" data-testid="settings-page">
        {/* Language Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="kpi-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 ship-gradient rounded-xl">
              <Translate size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Language', 'Idioma', 'Langue')}
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Change app language', 'Cambiar idioma de la aplicación', 'Change app language')}
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {[
              { code: 'en', flag: '🇺🇸', label: 'English' },
              { code: 'es', flag: '🇨🇷', label: 'Español' },
              { code: 'fr', flag: '🇫🇷', label: 'Français' },
            ].map(lang => (
              <Button
                key={lang.code}
                onClick={() => setLanguage(lang.code)}
                variant={language === lang.code ? 'default' : 'outline'}
                className={`flex-1 rounded-lg text-xs ${language === lang.code ? 'ship-gradient text-white' : ''}`}
                data-testid={`lang-${lang.code}-btn`}
              >
                {lang.flag} {lang.label}
              </Button>
            ))}
          </div>
        </motion.div>

        <PartnersSettings />

        {/* Shipping Locations & Currency */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.04 }}
          className="kpi-card"
          data-testid="locations-settings"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 ship-gradient rounded-xl">
              <MapPin size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Shipping Locations & Currency', 'Ubicaciones de Envio y Moneda', 'Emplacements et Devise')}
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Configure your warehouse and destination for the package flow', 'Configura tu almacen y destino para el flujo de paquetes', 'Configurez votre entrepot et destination')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">{tx('Warehouse Name', 'Nombre del Almacen', 'Nom de l\'entrepot')}</Label>
                <Input value={locations.warehouse_name || ''} onChange={e => setLocations(p => ({...p, warehouse_name: e.target.value}))} placeholder="Miami Warehouse" className="rounded-lg" data-testid="warehouse-name" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{tx('Warehouse City', 'Ciudad del Almacen', 'Ville de l\'entrepot')}</Label>
                <Input value={locations.warehouse_city || ''} onChange={e => setLocations(p => ({...p, warehouse_city: e.target.value}))} placeholder="Miami, FL" className="rounded-lg" data-testid="warehouse-city" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">{tx('Destination Name', 'Nombre del Destino', 'Nom de la Destination')}</Label>
                <Input value={locations.destination_name || ''} onChange={e => setLocations(p => ({...p, destination_name: e.target.value}))} placeholder="San Jose Office" className="rounded-lg" data-testid="destination-name" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{tx('Destination City', 'Ciudad del Destino', 'Ville de Destination')}</Label>
                <Input value={locations.destination_city || ''} onChange={e => setLocations(p => ({...p, destination_city: e.target.value}))} placeholder="San Jose, Costa Rica" className="rounded-lg" data-testid="destination-city" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs mb-1 block">{tx('Currency', 'Moneda', 'Devise')}</Label>
                <select value={locations.currency || 'USD'} onChange={e => setLocations(p => ({...p, currency: e.target.value}))} className="w-full border border-gray-200 rounded-lg p-2 text-sm" data-testid="currency-select">
                  {Object.entries(currencies).map(([code, info]) => (
                    <option key={code} value={code}>{info.symbol} {code} - {info.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">{tx('Weight Unit', 'Unidad de Peso', 'Unite de Poids')}</Label>
                <select value={locations.weight_unit || 'lbs'} onChange={e => setLocations(p => ({...p, weight_unit: e.target.value}))} className="w-full border border-gray-200 rounded-lg p-2 text-sm" data-testid="weight-unit-select">
                  <option value="lbs">Pounds (lbs)</option>
                  <option value="kg">Kilograms (kg)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">{tx('Price per Unit', 'Precio por Unidad', 'Prix par Unite')}</Label>
                <Input type="number" step="0.01" value={locations.price_per_unit || ''} onChange={e => setLocations(p => ({...p, price_per_unit: parseFloat(e.target.value) || 0}))} placeholder="4.50" className="rounded-lg" data-testid="price-per-unit" />
              </div>
            </div>
            <Button onClick={handleSaveLocations} disabled={savingLocations} className="ship-gradient text-white rounded-lg" data-testid="save-locations-btn">
              {savingLocations ? '...' : tx('Save Locations', 'Guardar Ubicaciones', 'Enregistrer les Emplacements')}
            </Button>
          </div>
        </motion.div>

        {/* Client Onboard Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.04 }}
          className="kpi-card"
          data-testid="onboard-form-settings"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 ship-gradient rounded-xl">
              <User size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Client Onboard Form', 'Formulario de Registro', 'Formulaire d\'Inscription')}
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Customize the sign-up form for new clients', 'Personaliza el formulario para nuevos clientes', 'Personnalisez le formulaire pour les nouveaux clients')}
              </p>
            </div>
          </div>

          {/* Shareable Link */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
            <Label className="text-xs mb-1 block font-semibold text-blue-700">
              {tx('Shareable Client Sign-Up Link', 'Enlace de Registro Compartible', 'Lien d\'Inscription Partageable')}
            </Label>
            <div className="flex gap-2">
              <Input 
                value={onboardLink || `${window.location.origin}/join/YOUR_COMPANY_ID`} 
                readOnly 
                className="rounded-lg bg-white text-sm font-mono flex-1" 
                data-testid="onboard-link-input"
              />
              <Button 
                onClick={() => { const link = onboardLink || `${window.location.origin}/join/`; navigator.clipboard.writeText(link); toast.success('Link copied!'); }}
                variant="outline" 
                className="rounded-lg"
                data-testid="copy-onboard-link-btn"
              >
                <Copy size={16} />
              </Button>
            </div>
            <p className="text-[10px] text-blue-500 mt-1.5">
              {tx('Share this link with potential clients. They\'ll fill out the form and get a unique tracking portal.', 'Comparte este enlace con clientes potenciales.', 'Partagez ce lien avec des clients potentiels.')}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs mb-1 block">{tx('Form Heading', 'Titulo del Formulario', 'Titre du Formulaire')}</Label>
              <Input value={onboardSettings.form_heading || ''} onChange={e => setOnboardSettings(p => ({...p, form_heading: e.target.value}))} placeholder="Create Your Account" className="rounded-lg" data-testid="onboard-heading" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">{tx('Welcome Message', 'Mensaje de Bienvenida', 'Message de Bienvenue')}</Label>
              <textarea 
                value={onboardSettings.welcome_message || ''} 
                onChange={e => setOnboardSettings(p => ({...p, welcome_message: e.target.value}))} 
                placeholder="Welcome! Sign up to start tracking your packages with us."
                className="w-full rounded-lg p-3 border border-gray-200 text-sm min-h-[60px] focus:outline-none focus:ring-1 focus:ring-[#FF9900] focus:border-[#FF9900]"
                data-testid="onboard-welcome-msg"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">{tx('Success Message', 'Mensaje de Exito', 'Message de Succès')}</Label>
              <textarea 
                value={onboardSettings.success_message || ''} 
                onChange={e => setOnboardSettings(p => ({...p, success_message: e.target.value}))} 
                placeholder="You're all set!"
                className="w-full rounded-lg p-3 border border-gray-200 text-sm min-h-[60px] focus:outline-none focus:ring-1 focus:ring-[#FF9900] focus:border-[#FF9900]"
                data-testid="onboard-success-msg"
              />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={onboardSettings.show_address} onChange={e => setOnboardSettings(p => ({...p, show_address: e.target.checked}))} className="rounded" data-testid="onboard-show-address" />
                {tx('Show Address Field', 'Mostrar Campo de Direccion', 'Afficher le Champ Adresse')}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={onboardSettings.show_city} onChange={e => setOnboardSettings(p => ({...p, show_city: e.target.checked}))} className="rounded" data-testid="onboard-show-city" />
                {tx('Show City Field', 'Mostrar Campo de Ciudad', 'Afficher le Champ Ville')}
              </label>
            </div>
            <Button onClick={handleSaveOnboard} disabled={savingOnboard} className="ship-gradient text-white rounded-lg" data-testid="save-onboard-btn">
              {savingOnboard ? '...' : tx('Save Onboard Settings', 'Guardar Configuración', 'Enregistrer les Paramètres')}
            </Button>
          </div>
        </motion.div>

        {/* Invoice & Receipt Customization */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.045 }}
          className="kpi-card"
          data-testid="invoice-settings"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 ship-gradient rounded-xl">
              <CurrencyDollar size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Invoice & Receipt Customization', 'Personalización de Facturas', 'Personnalisation des Factures')}
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Customize how your invoices and receipts look', 'Personaliza la apariencia de tus facturas', 'Personnalisez l\'apparence de vos factures')}
              </p>
            </div>
          </div>

          <p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 mb-4">
            {tx('Your company logo and primary color from Company Branding above will be used on all invoices automatically.', 'Tu logo y color de marca se usarán automáticamente.', 'Votre logo et couleur seront utilisés automatiquement.')}
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs mb-1 block">{tx('Invoice Footer Text', 'Texto del Pie de Factura', 'Texte du Pied de Facture')}</Label>
              <Input
                value={invoiceSettings.footer_text || ''}
                onChange={e => setInvoiceSettings(p => ({...p, footer_text: e.target.value}))}
                placeholder={tx('e.g., Thank you for your business! | Your Company Name', 'ej., Gracias por su compra!', 'ex., Merci pour votre achat!')}
                className="rounded-lg"
                data-testid="invoice-footer-text"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">{tx('Default Invoice Notes', 'Notas de Factura Predeterminadas', 'Notes de Facture par Défaut')}</Label>
              <textarea
                value={invoiceSettings.notes_template || ''}
                onChange={e => setInvoiceSettings(p => ({...p, notes_template: e.target.value}))}
                placeholder={tx('Default notes added to new invoices...', 'Notas predeterminadas para nuevas facturas...', 'Notes par défaut pour les nouvelles factures...')}
                className="w-full rounded-lg p-3 border border-gray-200 text-sm min-h-[60px] focus:outline-none focus:ring-1 focus:ring-[#FF9900] focus:border-[#FF9900]"
                data-testid="invoice-notes-template"
              />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={invoiceSettings.show_exchange_rate} onChange={e => setInvoiceSettings(p => ({...p, show_exchange_rate: e.target.checked}))} className="rounded" data-testid="invoice-show-exchange" />
                {tx('Show Exchange Rate', 'Mostrar Tipo de Cambio', 'Afficher le Taux de Change')}
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={invoiceSettings.show_crc_total} onChange={e => setInvoiceSettings(p => ({...p, show_crc_total: e.target.checked}))} className="rounded" data-testid="invoice-show-crc" />
                {tx('Show CRC Total', 'Mostrar Total en CRC', 'Afficher le Total en CRC')}
              </label>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={handleSaveInvoice} disabled={savingInvoice} className="ship-gradient text-white rounded-lg" data-testid="save-invoice-btn">
                {savingInvoice ? '...' : tx('Save Invoice Settings', 'Guardar Configuración de Factura', 'Enregistrer les Paramètres de Facture')}
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    const res = await api.get('/invoices/preview-branded-pdf', { responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    window.open(url, '_blank');
                  } catch (err) {
                    toast.error('Failed to generate preview');
                  }
                }}
                variant="outline"
                className="rounded-lg border-[#003166] text-[#003166] hover:bg-[#003166]/5"
                data-testid="preview-invoice-btn"
              >
                <Eye size={16} className="mr-1.5" />
                {tx('Preview Invoice', 'Vista Previa', 'Aperçu')}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Payment Accounts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
          className="kpi-card"
          data-testid="payment-accounts-settings"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl" style={{ background: 'linear-gradient(135deg, #635BFF, #0070BA)' }}>
              <CurrencyDollar size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{tx('Payment Accounts', 'Cuentas de Pago', 'Comptes de Paiement')}</h2>
              <p className="text-sm text-gray-500">{tx('Connect your Stripe or PayPal to receive client payments', 'Conecta tu Stripe o PayPal para recibir pagos de clientes', 'Connectez Stripe ou PayPal pour recevoir les paiements')}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4 mb-3">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${paymentAccounts.stripe_connected ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                {paymentAccounts.stripe_connected ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} />}
                Stripe {paymentAccounts.stripe_connected ? `(***${paymentAccounts.stripe_key_hint})` : tx('Not Connected', 'No Conectado', 'Non Connecté')}
              </div>
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium ${paymentAccounts.paypal_connected ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}>
                {paymentAccounts.paypal_connected ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} />}
                PayPal {paymentAccounts.paypal_connected ? `(***${paymentAccounts.paypal_id_hint})` : tx('Not Connected', 'No Conectado', 'Non Connecté')}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">{tx('Stripe API Key', 'Clave API de Stripe', 'Clé API Stripe')}</Label>
                <Input
                  type="password"
                  value={paymentForm.stripe_api_key}
                  onChange={e => setPaymentForm(prev => ({ ...prev, stripe_api_key: e.target.value }))}
                  placeholder={paymentAccounts.stripe_connected ? '••• Connected — enter new key to update' : 'sk_live_...'}
                  className="mt-1"
                  data-testid="payment-stripe-key"
                />
                <p className="text-xs text-gray-400 mt-1">Get from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-blue-500 underline">stripe.com/apikeys</a></p>
              </div>
              <div>
                <Label className="text-sm font-medium">{tx('PayPal Client ID', 'ID de Cliente PayPal', 'ID Client PayPal')}</Label>
                <Input
                  type="password"
                  value={paymentForm.paypal_client_id}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paypal_client_id: e.target.value }))}
                  placeholder={paymentAccounts.paypal_connected ? '••• Connected — enter new ID to update' : 'AX...'}
                  className="mt-1"
                  data-testid="payment-paypal-id"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">{tx('PayPal Secret', 'Secreto PayPal', 'Secret PayPal')}</Label>
                <Input
                  type="password"
                  value={paymentForm.paypal_secret}
                  onChange={e => setPaymentForm(prev => ({ ...prev, paypal_secret: e.target.value }))}
                  placeholder={paymentAccounts.paypal_connected ? '••• Connected — enter new secret to update' : 'EL...'}
                  className="mt-1"
                  data-testid="payment-paypal-secret"
                />
                <p className="text-xs text-gray-400 mt-1">Get from <a href="https://developer.paypal.com/dashboard/applications" target="_blank" rel="noreferrer" className="text-blue-500 underline">developer.paypal.com</a></p>
              </div>
              <div>
                <Label className="text-sm font-medium">{tx('Preferred Payment', 'Pago Preferido', 'Paiement Préféré')}</Label>
                <select
                  value={paymentForm.preferred_payment}
                  onChange={e => setPaymentForm(prev => ({ ...prev, preferred_payment: e.target.value }))}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  data-testid="payment-preferred"
                >
                  <option value="stripe">Stripe</option>
                  <option value="paypal">PayPal</option>
                  <option value="both">Both (show both options)</option>
                </select>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <strong>{tx('Auto-Invoice:', 'Auto-Factura:', 'Auto-Facture:')}</strong> {tx(
                'When a package status changes to "Ready for Pickup", an invoice is automatically generated with payment links from your connected accounts.',
                'Cuando el estado de un paquete cambia a "Listo para Recoger", se genera automáticamente una factura con links de pago de tus cuentas conectadas.',
                'Quand le statut d\'un colis passe à "Prêt pour Retrait", une facture est automatiquement générée avec des liens de paiement.'
              )}
            </div>

            <Button onClick={handleSavePaymentAccounts} disabled={savingPayment} className="ship-gradient text-white rounded-lg" data-testid="save-payment-btn">
              {savingPayment ? '...' : tx('Save Payment Accounts', 'Guardar Cuentas de Pago', 'Enregistrer les Comptes')}
            </Button>
          </div>
        </motion.div>

        {/* Company Branding */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="kpi-card"
          data-testid="branding-settings"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 ship-gradient rounded-xl">
              <PaintBrush size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Company Branding', 'Marca de la Empresa', 'Image de Marque')}
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Customize logo, color, and pricing', 'Personaliza logo, color y precios', 'Personnalisez le logo, les couleurs et les tarifs')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Logo Upload */}
            <div>
              <Label className="text-sm font-medium mb-2 block">{tx('Company Logo', 'Logo de la empresa', 'Logo de l\'Entreprise')}</Label>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="Company logo" className="w-full h-full object-contain" />
                  ) : (
                    <Image size={24} className="text-gray-400" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    ref={logoInputRef}
                    onChange={handleLogoUpload}
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    data-testid="logo-upload-input"
                  />
                  <Button
                    onClick={() => logoInputRef.current?.click()}
                    variant="outline"
                    className="rounded-lg text-sm"
                    data-testid="upload-logo-btn"
                  >
                    <Upload size={16} className="mr-1.5" />
                    {tx('Upload Logo', 'Subir Logo', 'Télécharger le Logo')}
                  </Button>
                  <p className="text-[10px] text-gray-400 mt-1">PNG, JPG, WebP. Max 2MB.</p>
                </div>
              </div>
            </div>

            {/* Primary Color */}
            <div>
              <Label className="text-sm font-medium mb-2 block">{tx('Primary Color', 'Color Primario', 'Couleur Principale')}</Label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={branding.primary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer"
                  data-testid="primary-color-input"
                />
                <Input
                  value={branding.primary_color}
                  onChange={(e) => setBranding(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="w-32 rounded-lg font-mono text-sm"
                  data-testid="primary-color-text"
                />
                <div className="w-8 h-8 rounded-lg" style={{ backgroundColor: branding.primary_color }} />
              </div>
            </div>

            {/* Company Name */}
            <div>
              <Label className="text-sm font-medium mb-2 block">{tx('Company Name', 'Nombre de Empresa', 'Nom de l\'Entreprise')}</Label>
              <Input
                value={branding.company_name}
                onChange={(e) => setBranding(prev => ({ ...prev, company_name: e.target.value }))}
                className="rounded-lg"
                data-testid="company-name-input"
              />
            </div>

            {/* Price per Pound */}
            <div>
              <Label className="text-sm font-medium mb-2 block">{tx('Price per Pound ($USD)', 'Precio por Libra ($USD)', 'Prix par Livre ($USD)')}</Label>
              <Input
                type="number"
                step="0.50"
                min="0"
                value={branding.price_per_pound}
                onChange={(e) => setBranding(prev => ({ ...prev, price_per_pound: parseFloat(e.target.value) || 0 }))}
                className="rounded-lg w-40"
                data-testid="price-per-pound-input"
              />
            </div>

            <Button
              onClick={handleSaveBranding}
              disabled={savingBranding}
              className="ship-gradient text-white rounded-lg"
              data-testid="save-branding-btn"
            >
              {savingBranding ? (tx('Saving...', 'Guardando...', 'Enregistrement...')) : (tx('Save Changes', 'Guardar Cambios', 'Enregistrer'))}
            </Button>
          </div>
        </motion.div>

        {/* Custom Domain */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.07 }}
          className="kpi-card"
          data-testid="domain-settings"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 ship-gradient rounded-xl">
              <Globe size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Custom Domain', 'Dominio Personalizado', 'Domaine Personnalisé')}
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Use your own domain for the customer portal', 'Usa tu propio dominio para el portal de clientes', 'Utilisez votre propre domaine pour le portail client')}
              </p>
            </div>
            {domainData?.status === 'verified' && (
              <div className="flex items-center gap-1.5">
                <ShieldCheck size={20} weight="fill" className="text-emerald-500" />
                <span className="text-xs font-semibold text-emerald-500">{tx('Verified', 'Verificado', 'Vérifié')}</span>
              </div>
            )}
          </div>

          {!domainHasFeature ? (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl">
              <p className="text-sm text-amber-800 font-medium">
                {tx('Custom domains are available on the Professional plan and above.', 'Los dominios personalizados están disponibles en el plan Profesional y superior.', 'Les domaines personnalisés sont disponibles à partir du plan Professionnel.')}
              </p>
              <Button
                onClick={() => window.location.href = '/settings'}
                variant="outline"
                className="mt-3 text-amber-700 border-amber-300 rounded-lg text-xs"
                data-testid="domain-upgrade-btn"
              >
                {tx('Upgrade Plan', 'Mejorar Plan', 'Mettre à niveau')}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Domain Input */}
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {tx('Your Domain', 'Tu Dominio', 'Votre Domaine')}
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    placeholder="track.yourcompany.com"
                    className="rounded-lg flex-1 font-mono text-sm"
                    disabled={!!domainData}
                    data-testid="domain-input"
                  />
                  {!domainData ? (
                    <Button
                      onClick={handleSetDomain}
                      disabled={domainLoading}
                      className="ship-gradient text-white rounded-lg"
                      data-testid="domain-save-btn"
                    >
                      {domainLoading ? '...' : tx('Save', 'Guardar', 'Enregistrer')}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleRemoveDomain}
                      variant="outline"
                      className="rounded-lg text-red-500 border-red-200 hover:bg-red-50"
                      data-testid="domain-remove-btn"
                    >
                      <Trash size={16} />
                    </Button>
                  )}
                </div>
              </div>

              {/* DNS Instructions */}
              {domainData && domainData.status !== 'verified' && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4">
                  <p className="text-sm font-semibold text-gray-800">
                    {tx('Configure these DNS records with your domain registrar:', 'Configura estos registros DNS con tu registrador de dominio:', 'Configurez ces enregistrements DNS chez votre registraire :')}
                  </p>

                  {/* TXT Record */}
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">TXT Record</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        domainData.status === 'txt_verified' || domainData.status === 'verified'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {domainData.status === 'txt_verified' || domainData.status === 'verified' ? tx('Verified', 'Verificado', 'Vérifié') : tx('Pending', 'Pendiente', 'En attente')}
                      </span>
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 w-12 flex-shrink-0">{tx('Host:', 'Host:', 'Hôte :')}</span>
                        <code className="bg-gray-50 px-2 py-1 rounded font-mono text-gray-800 flex-1 truncate">_trackmypackage.{domainData.domain}</code>
                        <button onClick={() => copyToClipboard(`_trackmypackage.${domainData.domain}`)} className="text-gray-400 hover:text-gray-600" data-testid="copy-txt-host">
                          <Copy size={14} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 w-12 flex-shrink-0">{tx('Value:', 'Valor:', 'Valeur :')}</span>
                        <code className="bg-gray-50 px-2 py-1 rounded font-mono text-gray-800 flex-1 truncate">{domainData.verification_token}</code>
                        <button onClick={() => copyToClipboard(domainData.verification_token)} className="text-gray-400 hover:text-gray-600" data-testid="copy-txt-value">
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* CNAME Record */}
                  <div className="bg-white rounded-lg p-3 border border-gray-100">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">CNAME Record</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        domainData.status === 'verified'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {domainData.status === 'verified' ? tx('Verified', 'Verificado', 'Vérifié') : tx('Pending', 'Pendiente', 'En attente')}
                      </span>
                    </div>
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 w-12 flex-shrink-0">{tx('Host:', 'Host:', 'Hôte :')}</span>
                        <code className="bg-gray-50 px-2 py-1 rounded font-mono text-gray-800 flex-1 truncate">{domainData.domain}</code>
                        <button onClick={() => copyToClipboard(domainData.domain)} className="text-gray-400 hover:text-gray-600" data-testid="copy-cname-host">
                          <Copy size={14} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 w-12 flex-shrink-0">{tx('Target:', 'Destino:', 'Cible :')}</span>
                        <code className="bg-gray-50 px-2 py-1 rounded font-mono text-gray-800 flex-1 truncate">{domainData.cname_target || 'app.trackmypackage.app'}</code>
                        <button onClick={() => copyToClipboard(domainData.cname_target || 'app.trackmypackage.app')} className="text-gray-400 hover:text-gray-600" data-testid="copy-cname-target">
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleVerifyDomain}
                      disabled={domainVerifying}
                      className="ship-gradient text-white rounded-lg text-sm"
                      data-testid="domain-verify-btn"
                    >
                      <ArrowClockwise size={16} className={`mr-1.5 ${domainVerifying ? 'animate-spin' : ''}`} />
                      {domainVerifying ? tx('Verifying...', 'Verificando...', 'Vérification...') : tx('Verify DNS', 'Verificar DNS', 'Vérifier DNS')}
                    </Button>
                    <p className="text-[10px] text-gray-400">
                      {tx('DNS changes can take up to 48 hours to propagate', 'Los cambios DNS pueden tardar hasta 48 horas en propagarse', 'Les changements DNS peuvent prendre jusqu\'à 48h pour se propager')}
                    </p>
                  </div>
                </div>
              )}

              {/* Verified State */}
              {domainData?.status === 'verified' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={20} weight="fill" className="text-emerald-600" />
                    <p className="text-sm font-semibold text-emerald-800">
                      {tx('Domain verified and active!', 'Dominio verificado y activo!', 'Domaine vérifié et actif !')}
                    </p>
                  </div>
                  <p className="text-xs text-emerald-600">
                    {tx(`Your customer portal is accessible at https://${domainData.domain}`, `Tu portal de clientes está accesible en https://${domainData.domain}`, `Votre portail client est accessible à https://${domainData.domain}`)}
                  </p>
                </div>
              )}
            </div>
          )}
        </motion.div>

        {/* API & Webhooks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.065 }}
          className="kpi-card"
          data-testid="api-webhooks-settings"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl" style={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)' }}>
              <ShieldCheck size={24} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('API & Webhooks', 'API y Webhooks', 'API et Webhooks')}
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Integrate your systems with our REST API', 'Integra tus sistemas con nuestra API REST', 'Integrez vos systemes avec notre API REST')}
              </p>
            </div>
          </div>

          <div className="space-y-5">
            {/* Generate API Key */}
            <div>
              <Label className="text-sm font-medium mb-2 block">{tx('API Keys', 'Claves API', 'Cles API')}</Label>
              <Button
                onClick={handleGenerateApiKey}
                className="ship-gradient text-white rounded-lg text-sm"
                data-testid="generate-api-key-btn"
              >
                {tx('Generate New API Key', 'Generar Nueva Clave API', 'Generer une Nouvelle Cle API')}
              </Button>
            </div>

            {/* Show newly generated key */}
            {generatedKey && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="generated-key-display">
                <p className="text-xs font-semibold text-emerald-700 mb-1">
                  {tx('New API Key (save it now — it won\'t be shown again):', 'Nueva Clave API (guardala ahora):', 'Nouvelle Cle API (sauvegardez-la maintenant):')}
                </p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-white rounded-lg px-3 py-2 text-sm font-mono text-gray-800 border border-emerald-200 break-all">
                    {generatedKey}
                  </code>
                  <Button
                    variant="outline"
                    className="rounded-lg shrink-0"
                    onClick={() => copyToClipboard(generatedKey)}
                    data-testid="copy-api-key-btn"
                  >
                    <Copy size={16} />
                  </Button>
                </div>
              </div>
            )}

            {/* Existing Keys */}
            {apiKeys.length > 0 && (
              <div>
                <Label className="text-xs mb-2 block text-gray-500">{tx('Active Keys', 'Claves Activas', 'Cles Actives')}</Label>
                <div className="space-y-2">
                  {apiKeys.map(k => (
                    <div key={k.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono text-gray-700">{k.prefix}...</code>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${k.active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-500'}`}>
                          {k.active ? tx('Active', 'Activo', 'Actif') : tx('Revoked', 'Revocado', 'Revoque')}
                        </span>
                      </div>
                      {k.active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeKey(k.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs h-7"
                          data-testid={`revoke-key-${k.id}`}
                        >
                          <Trash size={14} className="mr-1" />
                          {tx('Revoke', 'Revocar', 'Revoquer')}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* API Documentation Snippet */}
            <div className="bg-gray-900 rounded-xl p-4 text-sm" data-testid="api-docs-snippet">
              <p className="text-gray-400 text-xs mb-2">{tx('Usage Example:', 'Ejemplo de Uso:', 'Exemple d\'Utilisation:')}</p>
              <pre className="text-emerald-400 font-mono text-xs whitespace-pre-wrap overflow-x-auto">
{`curl -H "X-API-Key: your_key" \\
  ${window.location.origin}/api/v1/packages`}
              </pre>
              <div className="mt-3 pt-3 border-t border-gray-700 text-gray-400 text-xs space-y-1">
                <p><span className="text-blue-400">GET</span> /api/v1/packages — {tx('List packages', 'Listar paquetes', 'Lister les colis')}</p>
                <p><span className="text-emerald-400">POST</span> /api/v1/packages — {tx('Create package', 'Crear paquete', 'Creer un colis')}</p>
                <p><span className="text-blue-400">GET</span> /api/v1/customers — {tx('List customers', 'Listar clientes', 'Lister les clients')}</p>
                <p><span className="text-emerald-400">POST</span> /api/v1/customers — {tx('Create customer', 'Crear cliente', 'Creer un client')}</p>
                <p><span className="text-blue-400">GET</span> /api/v1/invoices — {tx('List invoices', 'Listar facturas', 'Lister les factures')}</p>
              </div>
            </div>

            {/* Webhook Config */}
            <div className="pt-4 border-t border-gray-100">
              <Label className="text-sm font-medium mb-2 block">{tx('Webhook URL', 'URL de Webhook', 'URL du Webhook')}</Label>
              <Input
                value={webhookUrl}
                onChange={e => setWebhookUrl(e.target.value)}
                placeholder="https://your-server.com/webhook"
                className="rounded-lg font-mono text-sm mb-3"
                data-testid="webhook-url-input"
              />
              <Label className="text-xs mb-2 block text-gray-500">{tx('Events to Subscribe', 'Eventos a Suscribir', 'Evenements a Souscrire')}</Label>
              <div className="flex flex-wrap gap-2 mb-3">
                {['package.created', 'package.status_updated', 'customer.created', 'invoice.created'].map(ev => (
                  <label key={ev} className="flex items-center gap-1.5 text-xs cursor-pointer bg-gray-50 px-2.5 py-1.5 rounded-lg border border-gray-100 hover:border-gray-300 transition-colors">
                    <input
                      type="checkbox"
                      checked={webhookEvents.includes(ev)}
                      onChange={e => {
                        if (e.target.checked) setWebhookEvents(prev => [...prev, ev]);
                        else setWebhookEvents(prev => prev.filter(x => x !== ev));
                      }}
                      className="rounded"
                      data-testid={`webhook-event-${ev}`}
                    />
                    <span className="font-mono text-gray-700">{ev}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveWebhook}
                  disabled={savingWebhook || !webhookUrl}
                  className="ship-gradient text-white rounded-lg text-sm"
                  data-testid="save-webhook-btn"
                >
                  {savingWebhook ? '...' : tx('Save Webhook', 'Guardar Webhook', 'Enregistrer le Webhook')}
                </Button>
                {webhookUrl && (
                  <Button
                    variant="outline"
                    onClick={handleDeleteWebhook}
                    className="rounded-lg text-red-500 border-red-200 hover:bg-red-50 text-sm"
                    data-testid="delete-webhook-btn"
                  >
                    <Trash size={14} className="mr-1" />
                    {tx('Remove', 'Eliminar', 'Supprimer')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* WhatsApp Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="kpi-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-500 rounded-xl">
              <WhatsappLogo size={24} className="text-white" weight="fill" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                WhatsApp
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Automatic customer notifications', 'Notificaciones automáticas a clientes', 'Automatic customer notifications')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {whatsappSettings?.enabled ? (
                <>
                  <CheckCircle size={24} weight="fill" className="text-green-500" />
                  <span className="text-sm font-medium text-green-600">
                    {tx('Active', 'Activo', 'Active')}
                  </span>
                </>
              ) : (
                <>
                  <XCircle size={24} weight="fill" className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">
                    {tx('Not configured', 'No configurado', 'Non configuré')}
                  </span>
                </>
              )}
            </div>
          </div>

          {whatsappSettings?.enabled ? (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-xl">
                <p className="text-sm text-green-800">
                  {tx('✅ WhatsApp is active. Customers will receive automatic notifications when their package status changes.', '✅ WhatsApp está activo. Los clientes recibirán notificaciones automáticas cuando cambie el estado de sus paquetes.', '✅ WhatsApp is active. Customers will receive automatic notifications when their package status changes.')}
                </p>
                {whatsappSettings.whatsapp_number && (
                  <p className="text-xs text-green-600 mt-2">
                    {tx('Sending number:', 'Número de envío:', 'Sending number:')} {whatsappSettings.whatsapp_number}
                  </p>
                )}
              </div>
              
              <div>
                <Label className="form-label">
                  {tx('Send test message', 'Enviar mensaje de prueba', 'Send test message')}
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+506 8888-1234"
                    className="rounded-lg"
                  />
                  <Button
                    onClick={handleTestWhatsApp}
                    disabled={testing}
                    className="bg-green-500 hover:bg-green-600 text-white rounded-lg"
                  >
                    <PaperPlaneTilt size={18} className="mr-2" />
                    {testing ? '...' : (tx('Test', 'Probar', 'Test'))}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-sm text-gray-600 mb-3">
                {tx('To enable WhatsApp, add these variables to the backend .env file:', 'Para activar WhatsApp, agrega las siguientes variables en el archivo .env del backend:', 'To enable WhatsApp, add these variables to the backend .env file:')}
              </p>
              <code className="block bg-gray-100 p-3 rounded-lg text-xs font-mono text-gray-700">
                TWILIO_ACCOUNT_SID=your_account_sid<br/>
                TWILIO_AUTH_TOKEN=your_auth_token<br/>
                TWILIO_WHATSAPP_NUMBER=whatsapp:+14155238886
              </code>
              <p className="text-xs text-gray-500 mt-3">
                {tx('Get your credentials at console.twilio.com', 'Obtén tus credenciales en console.twilio.com', 'Get your credentials at console.twilio.com')}
              </p>
            </div>
          )}
        </motion.div>

        {/* Email Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="kpi-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-500 rounded-xl">
              <EnvelopeSimple size={24} className="text-white" weight="fill" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                EMAIL
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Email notifications (Resend)', 'Notificaciones por correo (Resend)', 'Email notifications (Resend)')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {emailSettings?.enabled ? (
                <>
                  <CheckCircle size={24} weight="fill" className="text-green-500" />
                  <span className="text-sm font-medium text-green-600">
                    {tx('Active', 'Activo', 'Active')}
                  </span>
                </>
              ) : (
                <>
                  <XCircle size={24} weight="fill" className="text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">
                    {tx('Not configured', 'No configurado', 'Non configuré')}
                  </span>
                </>
              )}
            </div>
          </div>

          {emailSettings?.enabled ? (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-xl">
                <p className="text-sm text-blue-800">
                  {tx('✅ Email is active. Customers will receive automatic email notifications when an invoice is created or their package status changes.', '✅ Email está activo. Los clientes recibirán notificaciones automáticas por correo cuando se cree una factura o cambie el estado de sus paquetes.', '✅ Email is active. Customers will receive automatic email notifications when an invoice is created or their package status changes.')}
                </p>
                {emailSettings.sender_email && (
                  <p className="text-xs text-blue-600 mt-2">
                    {tx('Sender:', 'Remitente:', 'Sender:')} {emailSettings.sender_email}
                  </p>
                )}
              </div>
              
              <div>
                <Label className="form-label">
                  {tx('Send test email', 'Enviar email de prueba', 'Send test email')}
                </Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={testEmailAddr}
                    onChange={(e) => setTestEmailAddr(e.target.value)}
                    placeholder="test@example.com"
                    type="email"
                    className="rounded-lg"
                    data-testid="test-email-input"
                  />
                  <Button
                    onClick={handleTestEmail}
                    disabled={testingEmail}
                    className="bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
                    data-testid="test-email-btn"
                  >
                    <PaperPlaneTilt size={18} className="mr-2" />
                    {testingEmail ? '...' : (tx('Test', 'Probar', 'Test'))}
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <p className="text-xs uppercase tracking-wide text-gray-400 font-medium">
                  {tx('Automatic emails', 'Emails automáticos', 'Automatic emails')}
                </p>
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={16} className="text-green-500" weight="fill" />
                  {tx('On invoice creation (with public link)', 'Al crear factura (con link público)', 'On invoice creation (with public link)')}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <CheckCircle size={16} className="text-green-500" weight="fill" />
                  {tx('On package status change (configurable below)', 'Al cambiar estado de paquete (configurable abajo)', 'Au changement de statut (configurable ci-dessous)')}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-sm text-gray-600 mb-3">
                {tx('To enable email notifications, add these variables to the backend .env file:', 'Para activar notificaciones por email, agrega las siguientes variables en el archivo .env del backend:', 'To enable email notifications, add these variables to the backend .env file:')}
              </p>
              <code className="block bg-gray-100 p-3 rounded-lg text-xs font-mono text-gray-700">
                RESEND_API_KEY=re_your_api_key<br/>
                SENDER_EMAIL=info@trackingsystem.app
              </code>
              <p className="text-xs text-gray-500 mt-3">
                {tx('Get your API key at resend.com', 'Obtén tu API key en resend.com', 'Get your API key at resend.com')}
              </p>
            </div>
          )}

          {/* Per-status notification toggles — visible regardless of email config */}
          <div className="mt-6 pt-6 border-t border-gray-100 space-y-3 text-sm">
            <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
              {tx('Per-status email notifications', 'Notificaciones por estado', 'Notifications par statut')}
            </p>
            <p className="text-xs text-gray-500 -mt-1">
              {tx(
                'Choose which package status changes trigger an email to your customer. Emails are branded with your logo and brand color automatically.',
                'Elige qué cambios de estado activan un email al cliente. Los emails usan tu logo y color de marca automáticamente.',
                'Choisissez quels statuts déclenchent un e-mail. Les e-mails utilisent votre logo et couleur automatiquement.'
              )}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1" data-testid="status-notify-grid">
              {[
                { key: 'submitted',         label: tx('Package Submitted',  'Paquete Enviado',         'Colis soumis'),      color: '#64748B' },
                { key: 'intake_miami',      label: tx('Received in Warehouse','Recibido en Almacén',  'Reçu à l\'entrepôt'), color: '#2196F3' },
                { key: 'in_transit',        label: tx('In Transit',         'En Tránsito',             'En transit'),        color: '#FF9800' },
                { key: 'arrived_san_jose',  label: tx('Arrived at Destination','Llegó a Destino',     'Arrivé à destination'),color: '#009688' },
                { key: 'ready_for_pickup',  label: tx('Ready for Pickup',   'Listo para Recoger',      'Prêt à récupérer'),  color: '#4CAF50' },
                { key: 'delivered',         label: tx('Delivered',          'Entregado',                'Livré'),             color: '#8BC34A' },
              ].map(({ key, label, color }) => (
                <label
                  key={key}
                  className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 cursor-pointer transition-all"
                  data-testid={`status-notify-${key}`}
                >
                  <span className="flex items-center gap-2.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                    <span className="text-sm text-gray-700 truncate">{label}</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={statusNotify[key]}
                    onChange={e => setStatusNotify(p => ({ ...p, [key]: e.target.checked }))}
                    className="rounded shrink-0 h-4 w-4 accent-[#0077FF]"
                    data-testid={`status-notify-${key}-input`}
                  />
                </label>
              ))}
            </div>

            {/* Submission notification emails — alert admin/staff when a client submits a new tracking */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
                {tx('New Tracking Alerts', 'Alertas de Nuevos Trackings', 'Alertes de nouveaux suivis')}
              </p>
              <p className="text-xs text-gray-500 mt-1 mb-3">
                {tx(
                  "Get notified the moment a client submits a tracking via their portal. Admin accounts are notified automatically — add warehouse or staff emails below.",
                  'Recibe un correo cuando un cliente envíe un tracking desde su portal. Los administradores se notifican automáticamente — agrega correos de bodega o personal abajo.',
                  "Recevez un e-mail dès qu'un client soumet un suivi via son portail."
                )}
              </p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={submissionEmailInput}
                  onChange={(e) => setSubmissionEmailInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubmissionEmail(); } }}
                  placeholder="warehouse@yourcompany.com"
                  className="rounded-lg"
                  data-testid="submission-notify-email-input"
                />
                <Button
                  type="button"
                  onClick={addSubmissionEmail}
                  variant="outline"
                  className="rounded-lg whitespace-nowrap"
                  data-testid="submission-notify-add-btn"
                >
                  {tx('Add', 'Agregar', 'Ajouter')}
                </Button>
              </div>
              {submissionNotifyEmails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="submission-notify-list">
                  {submissionNotifyEmails.map(email => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 border border-sky-200 text-sky-700 text-xs"
                      data-testid={`submission-notify-pill-${email}`}
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeSubmissionEmail(email)}
                        className="text-sky-500 hover:text-red-500 ml-0.5"
                        data-testid={`submission-notify-remove-${email}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* ── Admin Internal Alerts ── New package created / New customer signed up ── */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
                {tx('Internal Admin Alerts', 'Alertas Internas Admin', 'Alertes admin internes')}
              </p>
              <p className="text-xs text-gray-500 mt-1 mb-3">
                {tx(
                  "Get notified whenever a new package is entered or a new client signs up. Add the emails that should receive these alerts (you, your dispatcher, ops manager...).",
                  'Recibe un correo cuando se ingresa un paquete o se registra un nuevo cliente. Agrega los correos que deben recibir las alertas.',
                  'Recevez un e-mail dès qu\'un nouveau colis ou client est créé.'
                )}
              </p>

              {/* Per-event toggles */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                <label className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                  <Checkbox
                    checked={notifyOnNewPackage}
                    onCheckedChange={(v) => setNotifyOnNewPackage(!!v)}
                    data-testid="notify-new-package-toggle"
                  />
                  <span className="text-sm">{tx('Alert on new package', 'Alerta al ingresar paquete', 'Alerte nouveau colis')}</span>
                </label>
                <label className="flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50">
                  <Checkbox
                    checked={notifyOnNewCustomer}
                    onCheckedChange={(v) => setNotifyOnNewCustomer(!!v)}
                    data-testid="notify-new-customer-toggle"
                  />
                  <span className="text-sm">{tx('Alert on new client', 'Alerta al crear cliente', 'Alerte nouveau client')}</span>
                </label>
              </div>

              {/* Recipient emails */}
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={adminAlertEmailInput}
                  onChange={(e) => setAdminAlertEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const v = (adminAlertEmailInput || '').trim();
                      if (!v || !/^\S+@\S+\.\S+$/.test(v)) {
                        toast.error(tx('Enter a valid email', 'Correo inválido', 'E-mail invalide'));
                        return;
                      }
                      if (adminAlertEmails.includes(v)) {
                        toast.error(tx('Already added', 'Ya agregado', 'Déjà ajouté'));
                        return;
                      }
                      if (adminAlertEmails.length >= 10) {
                        toast.error(tx('Max 10 emails', 'Máx 10 correos', 'Max 10'));
                        return;
                      }
                      setAdminAlertEmails([...adminAlertEmails, v]);
                      setAdminAlertEmailInput('');
                    }
                  }}
                  placeholder="GregMiceli2018@gmail.com"
                  className="rounded-lg"
                  data-testid="admin-alert-email-input"
                />
                <Button
                  type="button"
                  onClick={() => {
                    const v = (adminAlertEmailInput || '').trim();
                    if (!v || !/^\S+@\S+\.\S+$/.test(v)) {
                      toast.error(tx('Enter a valid email', 'Correo inválido', 'E-mail invalide'));
                      return;
                    }
                    if (adminAlertEmails.includes(v)) {
                      toast.error(tx('Already added', 'Ya agregado', 'Déjà ajouté'));
                      return;
                    }
                    if (adminAlertEmails.length >= 10) {
                      toast.error(tx('Max 10 emails', 'Máx 10 correos', 'Max 10'));
                      return;
                    }
                    setAdminAlertEmails([...adminAlertEmails, v]);
                    setAdminAlertEmailInput('');
                  }}
                  variant="outline"
                  className="rounded-lg whitespace-nowrap"
                  data-testid="admin-alert-add-btn"
                >
                  {tx('Add', 'Agregar', 'Ajouter')}
                </Button>
              </div>
              {adminAlertEmails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3" data-testid="admin-alert-list">
                  {adminAlertEmails.map(email => (
                    <span
                      key={email}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs"
                      data-testid={`admin-alert-pill-${email}`}
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => setAdminAlertEmails(adminAlertEmails.filter(e => e !== email))}
                        className="text-amber-500 hover:text-red-500 ml-0.5"
                        data-testid={`admin-alert-remove-${email}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {adminAlertEmails.length === 0 && (
                <p className="text-[11px] text-gray-400 mt-2 italic">
                  {tx(
                    'No emails added — alerts will fall back to your account email.',
                    'Sin correos — las alertas se envían al correo de tu cuenta.',
                    'Aucun e-mail — fallback sur l\'e-mail du compte.'
                  )}
                </p>
              )}
            </div>

            {/* ── Daily Digest ── One summary email per day instead of per-event pings ── */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
                    {tx('Daily Digest', 'Resumen Diario', 'Récap. quotidien')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1 mb-2">
                    {tx(
                      'One email per day summarising new signups, packages, invoices, and payments over the previous 24 hours. Great for busy days when per-event alerts would flood your inbox.',
                      'Un correo diario con resumen de altas, paquetes, facturas y pagos de las últimas 24h. Útil en días con mucha actividad.',
                      'Un e-mail par jour résumant les activités des dernières 24h.'
                    )}
                  </p>
                </div>
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-50 shrink-0">
                  <Checkbox
                    checked={digestEnabled}
                    onCheckedChange={(v) => setDigestEnabled(!!v)}
                    data-testid="daily-digest-toggle"
                  />
                  <span className="text-sm font-semibold">{digestEnabled ? tx('Enabled', 'Activo', 'Activé') : tx('Off', 'Off', 'Off')}</span>
                </label>
              </div>

              {digestEnabled && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs font-semibold text-gray-700">{tx('Send at (local hour)', 'Hora de envío (local)', 'Heure locale d\'envoi')}</Label>
                    <select
                      value={digestHour}
                      onChange={(e) => setDigestHour(Number(e.target.value))}
                      className="w-full rounded-lg mt-1 h-10 px-3 text-sm border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0077FF]/40"
                      data-testid="digest-hour-select"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold text-gray-700">{tx('Timezone', 'Zona horaria', 'Fuseau horaire')}</Label>
                    <select
                      value={digestTimezone}
                      onChange={(e) => setDigestTimezone(e.target.value)}
                      className="w-full rounded-lg mt-1 h-10 px-3 text-sm border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0077FF]/40"
                      data-testid="digest-tz-select"
                    >
                      <option value="America/Costa_Rica">America/Costa_Rica (UTC-6)</option>
                      <option value="America/New_York">America/New_York (UTC-5/4)</option>
                      <option value="America/Chicago">America/Chicago (UTC-6/5)</option>
                      <option value="America/Denver">America/Denver (UTC-7/6)</option>
                      <option value="America/Los_Angeles">America/Los_Angeles (UTC-8/7)</option>
                      <option value="America/Mexico_City">America/Mexico_City (UTC-6)</option>
                      <option value="America/Panama">America/Panama (UTC-5)</option>
                      <option value="Europe/Madrid">Europe/Madrid (UTC+1/2)</option>
                      <option value="UTC">UTC</option>
                    </select>
                  </div>
                </div>
              )}

              {digestEnabled && (
                <div className="flex items-center justify-between mt-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div className="text-xs text-blue-800">
                    {digestLastSent ? (
                      <>{tx('Last sent', 'Último enviado', 'Dernier envoi')}: <strong>{digestLastSent}</strong></>
                    ) : (
                      <em>{tx('No digest sent yet', 'Aún no enviado', 'Aucun envoi pour l\'instant')}</em>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={sendingDigestNow || adminAlertEmails.length === 0}
                    onClick={async () => {
                      if (adminAlertEmails.length === 0) {
                        toast.error(tx('Add at least one admin email above', 'Agrega al menos un correo arriba', 'Ajoutez d\'abord un e-mail'));
                        return;
                      }
                      setSendingDigestNow(true);
                      try {
                        const res = await api.post('/portal/settings/notifications/digest/send-now');
                        if (res.data.ok) {
                          const c = res.data.counts;
                          toast.success(tx(
                            `Test digest sent to ${res.data.recipients.join(', ')} — ${c.new_customers} clients · ${c.new_packages} pkgs · ${c.paid_invoices} paid`,
                            `Enviado a ${res.data.recipients.join(', ')} — ${c.new_customers} clientes · ${c.new_packages} paq.`,
                            `Envoyé à ${res.data.recipients.join(', ')}`
                          ), { duration: 8000 });
                        } else {
                          toast.info(tx(`Digest did not send: ${res.data.reason}`, `No enviado: ${res.data.reason}`, `Non envoyé: ${res.data.reason}`));
                        }
                      } catch (err) {
                        toast.error(err.response?.data?.detail || tx('Failed to send digest', 'Error al enviar', 'Échec'));
                      } finally {
                        setSendingDigestNow(false);
                      }
                    }}
                    className="rounded-lg text-xs border-blue-300 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                    data-testid="send-digest-now-btn"
                  >
                    {sendingDigestNow ? tx('Sending…', 'Enviando…', 'Envoi…') : tx('Send Test Digest Now', 'Enviar Prueba Ahora', 'Tester maintenant')}
                  </Button>
                </div>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSaveNotify}
                disabled={savingNotify}
                className="ship-gradient text-white rounded-lg"
                data-testid="save-notify-btn"
              >
                {savingNotify ? '...' : tx('Save Preferences', 'Guardar Preferencias', 'Enregistrer')}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Pricing Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="kpi-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-[#FFB300] rounded-xl">
              <CurrencyDollar size={24} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-lg" style={{ fontFamily: 'Bebas Neue' }}>
                {tx('Pricing', 'Tarifas', 'Tarifs')}
              </h3>
              <p className="text-sm text-gray-500">
                {tx('Pricing configuration', 'Configuración de precios', 'Pricing configuration')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                {tx('Price per kilo', 'Precio por kilo', 'Prix par Kilo')}
              </p>
              <p className="text-2xl font-bold text-[#0077FF]">
                ${pricing?.price_per_kg || 14.00}
              </p>
              <p className="text-sm font-semibold text-[#FFB300] mt-0.5" data-testid="price-per-kilo-crc">
                ≈ ₡{((pricing?.price_per_kg || 14) * (pricing?.exchange_rate_crc || 452.07)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} CRC
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">{tx('Rounded up to 0.5 kg', 'Redondeado a 0.5 kg', 'Arrondi à 0,5 kg')}</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-xl border border-amber-200" data-testid="small-package-fee-card">
              <p className="text-xs uppercase tracking-wide text-amber-700 mb-1 font-semibold">
                {pricing?.small_package_mode === 'flat'
                  ? tx('Small package flat fee', 'Tarifa fija paquete pequeño', 'Forfait petit colis')
                  : tx('Small package rate', 'Tarifa paquete pequeño', 'Tarif petit colis')}
              </p>
              <p className="text-2xl font-bold text-amber-700">
                ${(pricing?.small_package_flat_fee ?? 10).toFixed(2)}
                {pricing?.small_package_mode !== 'flat' && (
                  <span className="text-sm font-semibold text-amber-600 ml-1">/kg</span>
                )}
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5">
                {pricing?.small_package_mode === 'flat'
                  ? tx(
                      `Flat fee when actual weight < ${pricing?.small_package_threshold_kg ?? 1} kg`,
                      `Tarifa fija cuando el peso real < ${pricing?.small_package_threshold_kg ?? 1} kg`,
                      `Forfait si poids < ${pricing?.small_package_threshold_kg ?? 1} kg`
                    )
                  : tx(
                      `Per-kg rate when actual weight < ${pricing?.small_package_threshold_kg ?? 1} kg`,
                      `Tarifa por kg cuando peso real < ${pricing?.small_package_threshold_kg ?? 1} kg`,
                      `Tarif/kg si poids < ${pricing?.small_package_threshold_kg ?? 1} kg`
                    )}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl">
              <div className="flex items-start justify-between">
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  {tx('Exchange rate (live)', 'Tipo de cambio (en vivo)', 'Taux de change (live)')}
                </p>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const r = await api.post('/settings/pricing/refresh-fx');
                      toast.success(`Updated to ₡${r.data.rate} via ${r.data.source}`);
                      const res = await api.get('/settings/pricing');
                      setPricing(res.data);
                    } catch (err) {
                      toast.error('Could not fetch live rate — using cached value');
                    }
                  }}
                  className="text-[10px] font-semibold text-[#0077FF] hover:underline"
                  data-testid="refresh-fx-rate-btn"
                >
                  ↻ {tx('Refresh now', 'Actualizar', 'Actualiser')}
                </button>
              </div>
              <p className="text-2xl font-bold text-[#FFB300]" data-testid="exchange-rate-display">
                ₡{pricing?.exchange_rate_crc?.toFixed(2) || '452.07'}
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5">
                {tx('per USD', 'por USD', 'par USD')}
                {pricing?.exchange_rate_updated_at && (
                  <> · {tx('updated', 'actualizado', 'mis à jour')} {new Date(pricing.exchange_rate_updated_at).toLocaleDateString()}</>
                )}
                {pricing?.exchange_rate_source && (
                  <> · {pricing.exchange_rate_source}</>
                )}
              </p>
              <p className="text-[10px] text-gray-400">{tx('Auto-refreshes daily', 'Se actualiza diariamente', "Auto-actualisation quotidienne")}</p>
            </div>
          </div>

          {/* Editable tenant overrides */}
          <div className="mt-5 pt-5 border-t border-gray-100" data-testid="pricing-overrides-form">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-700 font-bold">
                  {tx('Pricing Overrides', 'Ajustes de Tarifa', 'Réglages de Tarif')}
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {tx(
                    'Set your own per-kg rate and small-package rule for this tenant.',
                    'Define la tarifa por kg y la regla de paquete pequeño para este tenant.',
                    "Définissez votre propre tarif par kg et la règle petit colis pour ce tenant."
                  )}
                </p>
              </div>
              {pricing?.is_tenant_override && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                  {tx('Custom', 'Personalizado', 'Personnalisé')}
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs font-semibold text-gray-700">{tx('Per Kilo (USD)', 'Por Kilo (USD)', 'Par Kilo (USD)')}</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricingDraft.price_per_kg}
                    onChange={(e) => setPricingDraft(p => ({ ...p, price_per_kg: e.target.value }))}
                    placeholder="14.00"
                    className="pl-7 rounded-lg"
                    data-testid="pricing-per-kg-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700">{tx('Small Pkg Threshold (kg)', 'Umbral Paquete Pequeño (kg)', 'Seuil Petit Colis (kg)')}</Label>
                <Input
                  type="number"
                  step="0.05"
                  min="0"
                  value={pricingDraft.small_package_threshold_kg}
                  onChange={(e) => setPricingDraft(p => ({ ...p, small_package_threshold_kg: e.target.value }))}
                  placeholder="1.0"
                  className="rounded-lg mt-1"
                  data-testid="pricing-threshold-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700">
                  {pricingDraft.small_package_mode === 'flat'
                    ? tx('Small Pkg Flat Fee (USD)', 'Tarifa Fija Pequeño (USD)', 'Forfait Petit (USD)')
                    : tx('Small Pkg Rate (USD/kg)', 'Tarifa Pequeño (USD/kg)', 'Tarif Petit (USD/kg)')}
                </Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={pricingDraft.small_package_flat_fee}
                    onChange={(e) => setPricingDraft(p => ({ ...p, small_package_flat_fee: e.target.value }))}
                    placeholder="10.00"
                    className="pl-7 rounded-lg"
                    data-testid="pricing-flat-fee-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-700">{tx('Small Pkg Mode', 'Modo Pequeño', 'Mode Petit')}</Label>
                <select
                  value={pricingDraft.small_package_mode}
                  onChange={(e) => setPricingDraft(p => ({ ...p, small_package_mode: e.target.value }))}
                  className="w-full rounded-lg mt-1 h-10 px-3 text-sm border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#0077FF]/40"
                  data-testid="pricing-mode-select"
                >
                  <option value="per_kg">{tx('Per kg (× weight)', 'Por kg (× peso)', 'Par kg (× poids)')}</option>
                  <option value="flat">{tx('Flat fee', 'Tarifa fija', 'Forfait fixe')}</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end mt-3 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  if (!window.confirm(tx(
                    'Re-price every package in the system using the current rules? Manually overridden packages will be kept. This will adjust customer balances accordingly.',
                    '¿Volver a calcular el precio de todos los paquetes con las reglas actuales? Los paquetes ajustados manualmente se mantendrán. Los saldos de los clientes se ajustarán.',
                    'Recalculer le prix de tous les colis ? Les ajustements manuels seront conservés.'
                  ))) return;
                  try {
                    const res = await api.post('/settings/pricing/recalc-all-packages');
                    toast.success(tx(
                      `Re-priced ${res.data.updated} packages · ${res.data.pending_invoices_repriced || 0} pending invoices updated · balance delta $${res.data.total_balance_delta.toFixed(2)}`,
                      `Recalculados ${res.data.updated} paquetes · ${res.data.pending_invoices_repriced || 0} facturas pendientes · delta $${res.data.total_balance_delta.toFixed(2)}`,
                      `Recalculé ${res.data.updated} colis · ${res.data.pending_invoices_repriced || 0} factures · delta $${res.data.total_balance_delta.toFixed(2)}`
                    ), { duration: 7000 });
                  } catch (err) {
                    toast.error(err.response?.data?.detail || tx('Failed to recalc prices', 'Error al recalcular', 'Échec'));
                  }
                }}
                className="rounded-lg"
                data-testid="recalc-all-prices-btn"
              >
                {tx('Re-price All Packages', 'Recalcular Todos los Paquetes', 'Recalculer Tous')}
              </Button>
              <Button
                onClick={handleSavePricing}
                disabled={savingPricing}
                className="ship-gradient text-white rounded-lg"
                data-testid="save-pricing-btn"
              >
                {savingPricing ? '...' : tx('Save Pricing', 'Guardar Tarifa', 'Enregistrer Tarif')}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Notification Templates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="kpi-card"
        >
          <h3 className="font-bold text-lg mb-4" style={{ fontFamily: 'Bebas Neue' }}>
            {tx('WhatsApp Templates', 'Plantillas de WhatsApp', 'WhatsApp Templates')}
          </h3>
          
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
              <p className="font-semibold text-blue-800">📦 Recibido Miami</p>
              <p className="text-blue-600 text-xs mt-1">
                {tx('Sent when package arrives in Miami', 'Se envía cuando el paquete llega a Miami', 'Sent when package arrives in Miami')}
              </p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg border-l-4 border-amber-500">
              <p className="font-semibold text-amber-800">✈️ En Tránsito</p>
              <p className="text-amber-600 text-xs mt-1">
                {tx('Sent when package leaves for Costa Rica', 'Se envía cuando sale hacia Costa Rica', 'Sent when package leaves for Costa Rica')}
              </p>
            </div>
            <div className="p-3 bg-teal-50 rounded-lg border-l-4 border-teal-500">
              <p className="font-semibold text-teal-800">🇨🇷 Llegó a San José</p>
              <p className="text-teal-600 text-xs mt-1">
                {tx('Sent when package arrives in Costa Rica', 'Se envía cuando llega a Costa Rica', 'Sent when package arrives in Costa Rica')}
              </p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
              <p className="font-semibold text-green-800">✅ Listo para Recoger</p>
              <p className="text-green-600 text-xs mt-1">
                {tx('Sent when ready for pickup', 'Se envía cuando está listo para recoger', 'Sent when ready for pickup')}
              </p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-lg border-l-4 border-emerald-500">
              <p className="font-semibold text-emerald-800">🎁 Entregado</p>
              <p className="text-emerald-600 text-xs mt-1">
                {tx('Sent when delivered to customer', 'Se envía cuando se entrega al cliente', 'Sent when delivered to customer')}
              </p>
            </div>
          </div>
        </motion.div>

        {/* ── DATA HEALTH: Find & cancel duplicate invoices ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="kpi-card"
          data-testid="duplicate-invoice-cleanup-card"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <h2 className="text-xl font-bold text-gray-800">{tx('Find Duplicate Invoices', 'Buscar Facturas Duplicadas', 'Trouver Factures en Double')}</h2>
              <p className="text-sm text-gray-500 mt-1">
                {tx(
                  'Scan all open invoices and find any customers whose packages have been billed more than once. Pick the duplicates to cancel — the original invoice is always kept.',
                  'Escanea todas las facturas abiertas y encuentra clientes facturados más de una vez por los mismos paquetes. La factura original siempre se conserva.',
                  'Recherche les factures en double et permet de les annuler en un clic.'
                )}
              </p>
            </div>
            <Button
              onClick={scanForDuplicateInvoices}
              disabled={scanningDupes}
              className="ship-gradient text-white rounded-lg whitespace-nowrap shrink-0"
              data-testid="scan-duplicates-btn"
            >
              {scanningDupes ? tx('Scanning…', 'Escaneando…', 'Analyse…') : tx('Scan Now', 'Escanear Ahora', 'Lancer le scan')}
            </Button>
          </div>

          {dupeReport && (dupeReport.groups || []).length === 0 && (
            <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm">
              {tx('Clean! No duplicate invoices found.', '¡Limpio! No hay facturas duplicadas.', 'Aucun doublon trouvé.')}
            </div>
          )}

          {dupeReport && (dupeReport.groups || []).length > 0 && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="text-sm text-amber-800">
                  <strong>{dupeReport.duplicate_invoice_count}</strong>{' '}
                  {tx('duplicate invoice(s) across', 'factura(s) duplicada(s) en', 'doublons sur')}{' '}
                  <strong>{dupeReport.affected_customers}</strong>{' '}
                  {tx('customer(s)', 'cliente(s)', 'client(s)')}
                </div>
                <Button
                  onClick={() => cancelDuplicateInvoices(
                    dupeReport.groups.flatMap(g => g.duplicates.map(d => d.id)),
                    'all'
                  )}
                  disabled={cancellingDupes}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
                  data-testid="cancel-all-duplicates-btn"
                >
                  {cancellingDupes
                    ? tx('Cancelling…', 'Cancelando…', 'Annulation…')
                    : tx('Cancel All Duplicates', 'Cancelar Todas', 'Tout annuler')}
                </Button>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {dupeReport.groups.map((g) => (
                  <div key={g.customer_id + '-' + g.keeper.id} className="p-3 rounded-lg border border-gray-200 bg-white">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="text-sm">
                        <strong>{g.customer_name}</strong>{' '}
                        <span className="text-gray-500">· {g.package_count} {tx('pkgs', 'paq.', 'colis')} · ${g.total_usd.toFixed(2)}</span>
                      </div>
                      <Button
                        onClick={() => cancelDuplicateInvoices(g.duplicates.map(d => d.id), g.customer_name)}
                        disabled={cancellingDupes}
                        variant="outline"
                        className="text-xs h-7 rounded-md border-red-200 text-red-700 hover:bg-red-50 shrink-0"
                        data-testid={`cancel-dupes-${g.customer_id}`}
                      >
                        {tx(`Cancel ${g.duplicates.length} extra`, `Cancelar ${g.duplicates.length}`, `Annuler ${g.duplicates.length}`)}
                      </Button>
                    </div>
                    <div className="text-[11px] grid grid-cols-1 md:grid-cols-2 gap-1.5">
                      <div className="flex items-center gap-2 p-2 rounded bg-emerald-50 border border-emerald-200">
                        <span className="text-[10px] uppercase font-bold text-emerald-700 px-1.5 py-0.5 rounded bg-emerald-200">KEEP</span>
                        <span className="font-mono">{g.keeper.invoice_number}</span>
                        <span className="text-gray-500 ml-auto">${g.keeper.total_usd.toFixed(2)}</span>
                      </div>
                      {g.duplicates.map((d) => (
                        <div key={d.id} className="flex items-center gap-2 p-2 rounded bg-red-50 border border-red-200">
                          <span className="text-[10px] uppercase font-bold text-red-700 px-1.5 py-0.5 rounded bg-red-200">DUPE</span>
                          <span className="font-mono">{d.invoice_number}</span>
                          <span className="text-gray-500 ml-auto">${d.total_usd.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </Layout>
  );
}
